/**
 * Worker API 클라이언트.
 *
 * 인증: admin 도메인은 Cloudflare Access로 보호되어, 브라우저 요청에
 * `CF_Authorization` 쿠키가 자동 첨부된다. 이 클라이언트는 자체 토큰 관리·
 * 로그인 UI를 두지 않으며 모든 fetch를 `credentials: 'include'`로 보낸다.
 *
 * 모든 요청·응답은 packages/shared의 Zod 스키마로 검증한다.
 */
import {
  AnalyticsViewsResponseSchema,
  BatchRequestSchema,
  BatchResponseSchema,
  DraftListResponseSchema,
  DraftSchema,
  DraftUpdateSchema,
  PublishRequestSchema,
  PublishResponseSchema,
  PublishedListResponseSchema,
  PublishedPostSchema,
  PublishedUpdateSchema,
  PublishedUpdateResponseSchema,
  TopicWarningsResponseSchema,
  UnpublishResponseSchema,
  type AnalyticsRange,
  type AnalyticsViewsResponse,
  type BatchRequest,
  type BatchResponse,
  type Draft,
  type DraftListResponse,
  type DraftUpdate,
  type PublishRequest,
  type PublishResponse,
  type PublishedListResponse,
  type PublishedPost,
  type PublishedUpdate,
  type PublishedUpdateResponse,
  type TopicWarningsResponse,
  type UnpublishResponse,
} from '@content-platform/shared';
import { z, type ZodType, type ZodTypeDef } from 'zod';

const CreateDraftResponseSchema = z.object({ draftId: z.string() });
export type CreateDraftResponse = z.infer<typeof CreateDraftResponseSchema>;

/**
 * production: 빈 값 → admin Pages 자기 도메인의 `/api/*`로 호출. Pages Function이
 *             Worker로 same-origin proxy해서 cross-origin cookie 문제를 회피한다.
 * local dev:  Vite proxy(`server.proxy['/api']`)가 worker(:8787)로 forward.
 *             별도 PUBLIC_API_URL 박을 필요 없음.
 *
 * 만약 도메인 결정 후 admin과 worker가 같은 zone 하위(예: admin.<root>, api.<root>)에
 * 매핑되면 PUBLIC_API_URL을 worker URL로 박아도 된다 (CF_Authorization 쿠키가 zone-wide).
 */
const BASE_URL = (import.meta.env.PUBLIC_API_URL ?? '').replace(/\/$/, '');

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    message: string,
    public readonly body: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/** 토스트/UI에 보여줄 에러 문구. 워커가 JSON body의 message를 보내면 그걸 우선. */
export function apiErrorMessage(err: unknown): string {
  if (err instanceof ApiError) {
    const body = err.body;
    if (typeof body === 'object' && body !== null && 'message' in body) {
      const m = (body as { message: unknown }).message;
      if (typeof m === 'string' && m.length > 0) return m;
    }
    return err.message;
  }
  return err instanceof Error ? err.message : String(err);
}

interface RequestOptions<T> extends Omit<RequestInit, 'body'> {
  body?: unknown;
  // ZodType의 3번째 type param(Input)은 unknown으로 둬서 .default()로 인해
  // Input ≠ Output인 스키마(예: DraftSchema의 body·tags)도 받을 수 있게 한다.
  schema?: ZodType<T, ZodTypeDef, unknown>;
}

async function request<T>(path: string, opts: RequestOptions<T> = {}): Promise<T> {
  const { schema, body, headers, ...rest } = opts;
  const hasJsonBody = body !== undefined && !(body instanceof FormData);

  const res = await fetch(`${BASE_URL}${path}`, {
    credentials: 'include',
    headers: {
      accept: 'application/json',
      ...(hasJsonBody ? { 'content-type': 'application/json' } : {}),
      ...headers,
    },
    body: hasJsonBody ? JSON.stringify(body) : (body as BodyInit | undefined),
    ...rest,
  });

  if (!res.ok) {
    const errBody = await res.json().catch(() => null);
    throw new ApiError(
      res.status,
      `${(rest.method ?? 'GET').toUpperCase()} ${path} → ${res.status}`,
      errBody,
    );
  }

  if (res.status === 204) return undefined as T;

  const json = await res.json();
  return schema ? schema.parse(json) : (json as T);
}

export const api = {
  runBatch: (req: BatchRequest = {}): Promise<BatchResponse> => {
    BatchRequestSchema.parse(req);
    return request('/api/batch', {
      method: 'POST',
      body: req,
      schema: BatchResponseSchema,
    });
  },

  listDrafts: (): Promise<DraftListResponse> =>
    request('/api/drafts', { schema: DraftListResponseSchema }),

  createDraft: (): Promise<CreateDraftResponse> =>
    request('/api/drafts', {
      method: 'POST',
      body: {},
      schema: CreateDraftResponseSchema,
    }),

  getDraft: (id: string): Promise<Draft> =>
    request(`/api/drafts/${encodeURIComponent(id)}`, { schema: DraftSchema }),

  updateDraft: (id: string, update: DraftUpdate): Promise<Draft> => {
    DraftUpdateSchema.parse(update);
    return request(`/api/drafts/${encodeURIComponent(id)}`, {
      method: 'PUT',
      body: update,
      schema: DraftSchema,
    });
  },

  deleteDraft: (id: string): Promise<void> =>
    request(`/api/drafts/${encodeURIComponent(id)}`, { method: 'DELETE' }),

  publish: (id: string, req: PublishRequest = {}): Promise<PublishResponse> => {
    PublishRequestSchema.parse(req);
    return request(`/api/publish/${encodeURIComponent(id)}`, {
      method: 'POST',
      body: req,
      schema: PublishResponseSchema,
    });
  },

  listPublished: (): Promise<PublishedListResponse> =>
    request('/api/published', { schema: PublishedListResponseSchema }),

  unpublish: (slug: string): Promise<UnpublishResponse> =>
    request(`/api/published/${encodeURIComponent(slug)}`, {
      method: 'DELETE',
      schema: UnpublishResponseSchema,
    }),

  getPublishedPost: (slug: string): Promise<PublishedPost> =>
    request(`/api/published/${encodeURIComponent(slug)}`, { schema: PublishedPostSchema }),

  updatePublished: (slug: string, update: PublishedUpdate): Promise<PublishedUpdateResponse> => {
    PublishedUpdateSchema.parse(update);
    return request(`/api/published/${encodeURIComponent(slug)}`, {
      method: 'PUT',
      body: update,
      schema: PublishedUpdateResponseSchema,
    });
  },

  listAnalyticsViews: (range: AnalyticsRange): Promise<AnalyticsViewsResponse> =>
    request(`/api/analytics/views?range=${range}`, {
      schema: AnalyticsViewsResponseSchema,
    }),

  getTopicWarnings: (threshold = 5): Promise<TopicWarningsResponse> =>
    request(`/api/topics/warnings?threshold=${threshold}`, {
      schema: TopicWarningsResponseSchema,
    }),
};
